import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import InputField from "@/components/shared/form/InputField";
import FormattedNumberInputField from "@/components/shared/form/FormattedNumberInputField";
import SelectInputField from "@/components/shared/form/SelectInputField";
import DateInputField from "@/components/shared/form/DateInputField";
import { SelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ClipboardPaste } from "lucide-react";
import {
  SheetAgentRunTaskType,
  GetSheetAgentRunByIdQuery,
} from "@/graphql/__generated__/graphql";
import useUpdateSheetAgentRunTaskMutationHook from "@/hooks/mutations/useUpdateSheetAgentRunTaskMutation";
import { parseDropdownOptions } from "./utils";
import { Badge } from "@/components/ui/badge";

type FieldSaveStatus = "idle" | "saving" | "saved" | "error";

// Timing constants
const DEBOUNCE_DELAY = 500; // Delay in milliseconds before triggering save on blur
const SAVED_STATUS_TIMEOUT = 2000; // Time in milliseconds to show "saved" status before resetting to "idle"
const ERROR_STATUS_TIMEOUT = 3000; // Time in milliseconds to show "error" status before resetting to "idle"

interface SheetTaskComponentProps {
  /** The task data from GraphQL */
  task: NonNullable<GetSheetAgentRunByIdQuery["getSheetAgentRunById"]>["tasks"][0];
  /** Whether the user has permission to manage the sheet agent */
  canManageSheetAgent: boolean;
  /** The index of the task */
  index: number;
}

/**
 * Creates a Zod schema for a single task field
 */
const createTaskSchema = (taskType: SheetAgentRunTaskType) => {
  switch (taskType) {
  case SheetAgentRunTaskType.Currency:
    return z.number().optional().nullable();
  case SheetAgentRunTaskType.Number:
    return z.number().int().optional().nullable();
  case SheetAgentRunTaskType.Date:
    return z.date().nullable().optional();
  case SheetAgentRunTaskType.Dropdown:
  case SheetAgentRunTaskType.Text:
  default:
    return z.string().optional().nullable();
  }
};

/**
 * SheetTaskComponent renders a single task with its input field and save logic.
 * Handles auto-save on blur (for text/number fields) or onChange (for date/dropdown).
 *
 * @component
 */
function SheetTaskComponent({ task, canManageSheetAgent, index }: SheetTaskComponentProps) {
  const { t } = useTranslation();
  const taskId = String(task.id);
  const { updateSheetAgentRunTaskMutation } = useUpdateSheetAgentRunTaskMutationHook();

  // Track original value to detect changes
  const originalValueRef = useRef<string | number | Date | null | undefined>(undefined);
  const [saveStatus, setSaveStatus] = useState<FieldSaveStatus>("idle");
  const isSavingRef = useRef(false);
  const isTypeSavingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create form schema for this single task
  const formSchema = useMemo(() => z.object({ [taskId]: createTaskSchema(task.type) }), [taskId, task.type]);
  type FormData = z.infer<typeof formSchema>;

  // Initialize default value based on task type and existing answer
  const defaultValue = useMemo(() => {
    const answer = task.answer;

    switch (task.type) {
    case SheetAgentRunTaskType.Date: {
      // Validate that answer is a valid date string before converting
      if (!answer) return null;
      const parsedDate = new Date(answer);
      // Check if the date is valid (Invalid Date has NaN as time value)
      return !isNaN(parsedDate.getTime()) ? parsedDate : null;
    }
    case SheetAgentRunTaskType.Currency:
    case SheetAgentRunTaskType.Number:
      if (answer === null || answer === undefined || answer === "") {
        // Use null instead of undefined so React Hook Form reliably overwrites any previous value
        return null;
      }
      return task.type === SheetAgentRunTaskType.Number ? parseInt(answer, 10) : parseFloat(answer);
    case SheetAgentRunTaskType.Dropdown:
    case SheetAgentRunTaskType.Text:
    default:
      return answer || "";
    }
  }, [task.answer, task.type]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { [taskId]: defaultValue },
    mode: "onChange",
  });

  // Set original value when component mounts or task changes
  useEffect(() => {
    originalValueRef.current = defaultValue;
    setSaveStatus("idle");
  }, [task.id, defaultValue]);

  // Reset form when task type changes
  useEffect(() => {
    form.reset({ [taskId]: defaultValue });
    originalValueRef.current = defaultValue;
  }, [task.type, taskId, form, defaultValue]);


  /**
   * Converts a form value to a string for the mutation
   */
  const valueToString = useCallback((value: string | number | Date | null | undefined): string => {
    if (value === undefined || value === null || value === "") {
      return "";
    }

    switch (task.type) {
    case SheetAgentRunTaskType.Date:
      if (value instanceof Date) {
        return value.toISOString();
      }
      return "";
    case SheetAgentRunTaskType.Currency:
    case SheetAgentRunTaskType.Number:
      return String(value);
    case SheetAgentRunTaskType.Dropdown:
    case SheetAgentRunTaskType.Text:
    default:
      return String(value);
    }
  }, [task.type]);

  /**
   * Compares two values to check if they're different
   */
  const valuesAreEqual = useCallback((
    value1: string | number | Date | null | undefined,
    value2: string | number | Date | null | undefined
  ): boolean => {
    const v1 = value1 === "" || value1 === undefined || value1 === null ? undefined : value1;
    const v2 = value2 === "" || value2 === undefined || value2 === null ? undefined : value2;

    if (v1 === undefined && v2 === undefined) return true;
    if (v1 === undefined || v2 === undefined) return false;

    if (task.type === SheetAgentRunTaskType.Date) {
      const d1 = v1 instanceof Date ? v1.toISOString() : String(v1);
      const d2 = v2 instanceof Date ? v2.toISOString() : String(v2);
      return d1 === d2;
    }

    if (task.type === SheetAgentRunTaskType.Currency || task.type === SheetAgentRunTaskType.Number) {
      return Number(v1) === Number(v2);
    }

    return String(v1) === String(v2);
  }, [task.type]);

  /**
   * Helper function to manage save status with timeout
   */
  const manageSaveStatus = useCallback((newStatus: FieldSaveStatus) => {
    setSaveStatus(newStatus);

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (newStatus === "saved") {
      // Clear saved status after timeout
      timeoutRef.current = setTimeout(() => {
        setSaveStatus((prev) => (prev === "saved" ? "idle" : prev));
        timeoutRef.current = null;
      }, SAVED_STATUS_TIMEOUT);
    } else if (newStatus === "error") {
      // Clear error status after timeout
      timeoutRef.current = setTimeout(() => {
        setSaveStatus((prev) => (prev === "error" ? "idle" : prev));
        timeoutRef.current = null;
      }, ERROR_STATUS_TIMEOUT);
    }
  }, []);

  /**
   * Saves the task field value
   */
  const saveTaskField = useCallback(async () => {
    if (isSavingRef.current || !canManageSheetAgent) return;

    const currentValue = form.getValues(taskId as keyof FormData) as string | number | Date | null | undefined;
    const originalValue = originalValueRef.current;

    // Check if value actually changed
    if (valuesAreEqual(currentValue, originalValue)) {
      return;
    }

    isSavingRef.current = true;
    manageSaveStatus("saving");

    try {
      const answerString = valueToString(currentValue);
      await updateSheetAgentRunTaskMutation({
        variables: {
          updateSheetAgentRunTaskId: task.id,
          answer: answerString,
          type: task.type,
        },
      });

      originalValueRef.current = currentValue;
      manageSaveStatus("saved");
    } catch (_err) {
      manageSaveStatus("error");
    } finally {
      isSavingRef.current = false;
    }
  }, [form, taskId, task.id, updateSheetAgentRunTaskMutation, valueToString, valuesAreEqual, canManageSheetAgent, manageSaveStatus]);

  // Debounced save for blur events
  const debouncedSave = useMemo(
    () => debounce(() => {
      saveTaskField();
    }, DEBOUNCE_DELAY),
    [saveTaskField]
  );

  /**
   * Handles blur event - triggers debounced save
   */
  const handleBlur = useCallback(() => {
    debouncedSave();
  }, [debouncedSave]);

  /**
   * Handles onChange - saves immediately (for date and dropdown)
   */
  const handleChange = useCallback(() => {
    saveTaskField();
  }, [saveTaskField]);

  /**
   * Handles pasting text from clipboard.
   * For numeric fields, only accepts plain numeric strings (no currency symbols or locale formatting).
   * For text fields, pastes the raw trimmed string.
   */
  const handlePaste = useCallback(async () => {
    if (!canManageSheetAgent) return;
    try {
      const text = await navigator.clipboard.readText();
      const trimmed = text.trim();

      if (!trimmed) {
        return;
      }

      if (task.type === SheetAgentRunTaskType.Currency || task.type === SheetAgentRunTaskType.Number) {
        // Only accept plain numeric strings without currency symbols or locale-specific separators.
        // Examples that are accepted: "123", "-123.45"
        // Examples that are rejected: "€123", "1,234.56", "12.541,48"
        const numericPattern = /^-?\d+(\.\d+)?$/;
        if (!numericPattern.test(trimmed)) {
          return;
        }

        const numericValue =
          task.type === SheetAgentRunTaskType.Number ? parseInt(trimmed, 10) : parseFloat(trimmed);

        if (Number.isNaN(numericValue)) {
          return;
        }

        form.setValue(taskId as keyof FormData, numericValue as any, { shouldValidate: true });
      } else {
        form.setValue(taskId as keyof FormData, trimmed as any, { shouldValidate: true });
      }

      await saveTaskField();
    } catch (error) {
      console.error("Failed to read from clipboard:", error);
    }
  }, [canManageSheetAgent, task.type, taskId, form, saveTaskField]);

  /**
   * Saves the task type and clears the answer
   */
  const saveTaskType = useCallback(async (newType: SheetAgentRunTaskType) => {
    if (isTypeSavingRef.current || !canManageSheetAgent || newType === task.type) return;

    isTypeSavingRef.current = true;
    manageSaveStatus("saving");

    // Clear the form value IMMEDIATELY before mutation to prevent rendering with incompatible value
    const clearedValue =
      newType === SheetAgentRunTaskType.Currency ||
        newType === SheetAgentRunTaskType.Number ||
        newType === SheetAgentRunTaskType.Date
        ? null
        : "";
    form.reset({ [taskId]: clearedValue as any });
    originalValueRef.current = clearedValue as any;

    try {
      await updateSheetAgentRunTaskMutation({
        variables: {
          updateSheetAgentRunTaskId: task.id,
          type: newType,
          answer: "", // Clear the answer when type changes
        },
      });

      manageSaveStatus("saved");
    } catch (_err) {
      manageSaveStatus("error");
    } finally {
      isTypeSavingRef.current = false;
    }
  }, [task.id, task.type, taskId, updateSheetAgentRunTaskMutation, canManageSheetAgent, manageSaveStatus, form]);

  /**
   * Handles type change
   */
  const handleTypeChange = useCallback((newType: string) => {
    const typedNewType = newType as SheetAgentRunTaskType;
    saveTaskType(typedNewType);
  }, [saveTaskType]);

  /**
   * Gets the status text for a given save status
   */
  const getStatusText = useCallback((status: FieldSaveStatus): string | null => {
    switch (status) {
    case "saving":
      return t("edittender.sheetagent.task.status.saving");
    case "saved":
      return t("edittender.sheetagent.task.status.saved");
    case "error":
      return t("edittender.sheetagent.task.status.errorsaving");
    default:
      return null;
    }
  }, [t]);

  /**
   * Gets the status text color class for a given save status
   */
  const getStatusTextClass = useCallback((status: FieldSaveStatus): string => {
    switch (status) {
    case "saving":
      return "text-muted-foreground";
    case "saved":
      return "text-green-600 dark:text-green-400";
    case "error":
      return "text-destructive";
    default:
      return "";
    }
  }, []);

  // Check if dropdown options exist
  const hasDropdownOptions = useMemo(() => {
    const options = parseDropdownOptions(task.dropdownOptions);
    return options.length > 0;
  }, [task.dropdownOptions]);

  // Get available task types for the dropdown
  const availableTaskTypes = useMemo(() => {
    const allTypes = [
      { value: SheetAgentRunTaskType.Text, label: t("common:sheetagenttasktype.text") },
      { value: SheetAgentRunTaskType.Currency, label: t("common:sheetagenttasktype.currency") },
      { value: SheetAgentRunTaskType.Number, label: t("common:sheetagenttasktype.number") },
      { value: SheetAgentRunTaskType.Date, label: t("common:sheetagenttasktype.date") },
      { value: SheetAgentRunTaskType.Dropdown, label: t("common:sheetagenttasktype.dropdown") },
    ];

    // If no dropdown options exist, dropdown type is not allowed.
    // If dropdown options do exist, only the dropdown type is allowed.
    if (!hasDropdownOptions) {
      return allTypes.filter((type) => type.value !== SheetAgentRunTaskType.Dropdown);
    }

    return allTypes.filter((type) => type.value === SheetAgentRunTaskType.Dropdown);
  }, [hasDropdownOptions, t]);

  const statusText = getStatusText(saveStatus);
  const statusClass = getStatusTextClass(saveStatus);

  // Render appropriate input field
  const renderInputField = () => {
    const commonProps = {
      form,
      name: taskId,
      className: "flex-1",
      disabled: !canManageSheetAgent,
    };

    switch (task.type) {
    case SheetAgentRunTaskType.Currency:
      return (
        <FormattedNumberInputField
          {...commonProps}
          control={form.control}
          style="currency"
          currency="EUR"
          placeholder="0.00"
          onBlur={handleBlur}
        />
      );

    case SheetAgentRunTaskType.Number:
      return (
        <FormattedNumberInputField
          {...commonProps}
          control={form.control}
          isInteger={true}
          style="decimal"
          placeholder="0"
          onBlur={handleBlur}
        />
      );

    case SheetAgentRunTaskType.Dropdown: {
      const options = parseDropdownOptions(task.dropdownOptions);
      return (
        <SelectInputField
          {...commonProps}
          placeholder={t("edittender.sheetagent.task.placeholders.selectoption")}
          onChange={handleChange}
        >
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectInputField>
      );
    }

    case SheetAgentRunTaskType.Date:
      return (
        <DateInputField
          {...commonProps}
          allowOlderDates={true}
          onChange={handleChange}
        />
      );

    case SheetAgentRunTaskType.Text:
    default:
      return (
        <InputField
          {...commonProps}
          type="text"
          placeholder={t("edittender.sheetagent.task.placeholders.enteryouranswer")}
          onBlur={handleBlur}
        />
      );
    }
  };

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Form {...form}>
      <div
        className="
          space-y-2 border p-3 rounded-md shadow-sm
          transition-all duration-100
          hover:border-foreground/30 hover:bg-accent/20
          focus-within:border-foreground/40 focus-within:bg-accent/30
        "
      >
        <div className="flex items-center justify-between gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary">{task.cell}</Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-[200px]">
              <p>{t("edittender.sheetagent.task.cell.tooltip")}</p>
            </TooltipContent>
          </Tooltip>
          {statusText && (
            <span className={`text-xs ${statusClass}`}>
              {statusText}
            </span>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Select
              value={task.type}
              onValueChange={handleTypeChange}
              disabled={!canManageSheetAgent || isTypeSavingRef.current}
            >
              <SelectTrigger className="w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTaskTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <label className="text-sm block">
          <span className="font-bold">{index + 1}. </span>
          {task.question}
        </label>
        <div className="flex items-center gap-2">
          {renderInputField()}
          {task.type !== SheetAgentRunTaskType.Dropdown && task.type !== SheetAgentRunTaskType.Date && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handlePaste}
              disabled={!canManageSheetAgent}
              className="shrink-0"
              aria-label={`Paste answer for ${task.question}`}
            >
              <ClipboardPaste className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
}

export default SheetTaskComponent;
